import {Plot} from './plot';
import {Summary} from './summary';
import {StepIndicator} from './stepIndicator';
import initMap from './map';
import {EquipmentCategory} from './equipmentCategory';

export default class Configurator {

	activeStep: number;
	selectedProject: number | null;
	selectedPlot: number | null;
	private wrapper: HTMLDivElement;
	private stepIndicator: StepIndicator;
	private readonly step1: HTMLDivElement;
	private readonly step2: HTMLDivElement;
	private readonly step3: HTMLDivElement;
	private step4: HTMLDivElement;

	private summary: Summary = new Summary;
	private imagePopup: HTMLDialogElement;
	private popupImage: HTMLImageElement;

	private resizeListeners: Array<() => void> = [];

	constructor(wrapper: HTMLDivElement) {
		this.wrapper = wrapper;

		this.stepIndicator = new StepIndicator(wrapper, parseInt(wrapper.dataset.steps));
		this.activeStep = parseInt(wrapper.dataset.step);
		this.selectedProject = wrapper.dataset.selectedProject ? parseInt(wrapper.dataset.selectedProject) : null;
		this.selectedPlot = wrapper.dataset.selectedPlot ? parseInt(wrapper.dataset.selectedPlot) : null;

		this.step1 = this.wrapper.querySelector('#configurator-step-1');
		this.step2 = this.wrapper.querySelector('#configurator-step-2');
		this.step3 = this.wrapper.querySelector('#configurator-step-3');
		this.step4 = this.wrapper.querySelector('#configurator-step-4');

		this.imagePopup = document.getElementById('configurator-image-popup') as HTMLDialogElement;
		this.popupImage = this.imagePopup.querySelector('img');
	}

	init() {
		this.initSummary();

		// Close dialog on clicking on the close button
		this.imagePopup.querySelector('.close-btn').addEventListener('click', () => {
			this.imagePopup.close();
		});
		// Close dialog on clicking on backdrop
		this.imagePopup.addEventListener('click', e => {
			const rect = this.imagePopup.getBoundingClientRect();
			const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height && rect.left <= e.clientX && e.clientX <= rect.left + rect.width);
			if (!isInDialog) {
				this.imagePopup.close();
			}
		});

		this.stepIndicator.setStep(this.activeStep);
		switch (this.activeStep) {
			case 1:
				this.initStep1();
				break;
			case 2:
				this.loadStep2().then(() => {
					this.initStep2();
				});
				break;
			case 3:
				this.initStep3();
				break;
			case 4:
				this.initStep4();
				break;
		}
		this.initContactForm();

		this.wrapper.addEventListener('step-change', (e: CustomEvent<number>) => {
			switch (e.detail) {
				case 1:
					this.loadStep1().then(() => {
						this.initStep1();
					});
					break;
				case 2:
					this.loadStep2().then(() => {
						this.initStep2();
					});
					break;
				case 3:
					this.loadStep3().then(() => {
						this.initStep3();
					});
					break;
			}
		});
	}

	initSummary() {
		const params = new URLSearchParams(window.location.search);
		params.forEach((value, key) => {
			if (key.startsWith('e')) {
				this.summary.setPrice(parseInt(key.replace('e', '')), parseInt(value));
			}
		});
	}

	selectProject(id: number) {
		this.selectedProject = id;
		this.wrapper.setAttribute('data-selected-project', id.toString());
		this.activeStep = 2;
	}

	selectPlot(id: number) {
		this.selectedPlot = id;
		this.wrapper.setAttribute('data-selected-plot', id.toString());
		this.activeStep = 3;
	}

	initStep1() {
		this.initCarousel();
		this.clearResizeListeners();

		this.selectedPlot = null;

		(this.wrapper.querySelectorAll('.project-select') as NodeListOf<HTMLButtonElement>).forEach(btn => {
			btn.addEventListener('click', () => {
				this.selectProject(parseInt(btn.dataset.id));
				this.loadStep2().then(() => {
					this.initStep2();
				});
				this.stepIndicator.setStep(this.activeStep);
				this.updateHistory();
			});
		});

		this.step1.querySelectorAll('.discount-banner').forEach(banner => {
			banner.addEventListener('click', () => {
				this.showDiscountPopup();
			});
		});

		(this.wrapper.querySelectorAll('.contact-realter') as NodeListOf<HTMLButtonElement>).forEach(btn => {
			btn.addEventListener('click', () => {
				this.wrapper.classList.add('show-contact');
				window.scrollTo({
					top: this.wrapper.querySelector('.contact-wrapper').getBoundingClientRect().top + window.scrollY - 200,
					behavior: 'smooth',
				});
			});
		});

		this.stepIndicator.initStepClick(this.step1);

		this.initMap();

		const switchListBtn = this.step1.querySelector<HTMLButtonElement>('.show-list');
		const switchMapBtn = this.step1.querySelector<HTMLButtonElement>('.show-map');
		const listWrapper = this.step1.querySelector<HTMLDivElement>('.projects');
		const mapWrapper = this.step1.querySelector<HTMLDivElement>('.projects-map');

		if (switchListBtn && switchMapBtn && listWrapper && mapWrapper) {
			let map : SvgPanZoom.Instance|null = null;
			switchListBtn.addEventListener('click', () => {
				switchMapBtn.disabled = false;
				switchListBtn.disabled = true;
				listWrapper.classList.remove('hidden');
				mapWrapper.classList.add('hidden');
			});
			switchMapBtn.addEventListener('click', () => {
				switchMapBtn.disabled = true;
				switchListBtn.disabled = false;
				listWrapper.classList.add('hidden');
				mapWrapper.classList.remove('hidden');
				if (!map) {
					map = initMap()
				}
				map.reset();
			});
		}
	}

	private initMap() {
		const mapWrapper = this.step1.querySelector<HTMLDivElement>('.projects-map');
		if (!mapWrapper) {
			return;
		}

		// @ts-ignore
		const map = document.getElementById("czech-map") as SVGElement;
		const locations = map.querySelectorAll<SVGPathElement>('.project-point');
		const tooltips = mapWrapper.querySelector('.tooltips') as HTMLDivElement;

		locations.forEach(location => {
			const id = parseInt(location.dataset.id);
			const tooltip = document.createElement('div');
			tooltip.classList.add('tooltip');
			tooltip.innerText = location.dataset.name;
			tooltips.appendChild(tooltip);

			const overlay = mapWrapper.querySelector<HTMLDivElement>(`.overlays #project-${id}-overlay`);


			location.addEventListener('mouseenter', () => {
				if (location.classList.contains('selected')) {
					return;
				}

				tooltips.appendChild(tooltip);
				tooltip.classList.add('show');
			});
			location.addEventListener('mousemove', e => {
				const svgPos = map.getBoundingClientRect();

				tooltip.style.top = `${e.y - svgPos.top}px`;
				tooltip.style.left = `${e.x - svgPos.left}px`;
			});
			location.addEventListener('mouseleave', () => {
				tooltip.classList.remove('show');
			});
			location.addEventListener('click', () => {
				// Hide existing overlay
				const activeOverlays = mapWrapper.querySelectorAll<HTMLDivElement>('.overlay:not(.hidden)');
				for (const activeOverlay of activeOverlays) {
					activeOverlay.classList.remove('show');
					activeOverlay.classList.add('hidden');
				}

				// Disable selected class from all locations
				const activeLocations = mapWrapper.querySelectorAll<SVGPathElement>('.project-point.selected');
				for (const activeLocation of activeLocations) {
					activeLocation.classList.remove('selected');
				}

				// Show overlay
				location.classList.add('selected');
				overlay.classList.add('show');
				overlay.classList.remove('hidden');
			});
		});
	}

	async loadStep1() {
		// Start loader
		const loader = this.createLoader();
		this.step1.appendChild(loader);

		const params: {
			[index: string]: number | string
		} = Object.fromEntries(new URLSearchParams(window.location.search));
		params.project = this.selectedProject;
		const response = await this.loadStep(1, params);
		if (response.ok) {
			this.step1.innerHTML = await response.text();
		} else {
			// TODO: Handle error
		}

		// Stop loading
		loader.remove();
	}

	async loadStep2() {
		// Start loader
		const loader = this.createLoader();
		this.step2.appendChild(loader);

		const params: {
			[index: string]: number | string
		} = Object.fromEntries(new URLSearchParams(window.location.search));
		params.project = this.selectedProject;
		params.plot = this.selectedPlot;
		const response = await this.loadStep(2, params);
		if (response.ok) {
			this.step2.innerHTML = await response.text();
			this.summary.update();
		} else {
			// TODO: Handle error
		}

		// Stop loading
		loader.remove();
	}

	async loadStep3() {
		// Start loader
		const loader = this.createLoader();
		this.step3.appendChild(loader);

		const params: {
			[index: string]: number | string
		} = Object.fromEntries(new URLSearchParams(window.location.search));
		params.project = this.selectedProject;
		params.plot = this.selectedPlot;
		const response = await this.loadStep(3, params);
		if (response.ok) {
			this.step3.innerHTML = await response.text();
			this.summary.update();
		} else {
			// TODO: Handle error
		}

		// Stop loading
		loader.remove();
	}

	async loadStep4() {
		// Start loader
		const loader = this.createLoader();
		this.step4.appendChild(loader);

		const params = this.summary.getHistoryParamsObject();
		params.project = this.selectedProject;
		params.plot = this.selectedPlot;
		const response = await this.loadStep(4, params);
		if (response.ok) {
			this.step4.innerHTML = await response.text();
			this.summary.update();
		} else {
			// TODO: Handle error
		}

		// Stop loading
		loader.remove();
	}

	initStep2() {
		this.initCarousel();
		this.initBackBtn();
		this.clearResizeListeners();

		const svg = this.step2.querySelector('svg.plot-select-overlay') as SVGSVGElement;
		const plots = this.step2.querySelectorAll('.plot-polygon') as NodeListOf<SVGPathElement>;
		const projectInfoWrapper = this.step2.querySelector('.project-info-wrapper') as HTMLDivElement;

		const projectInfo = projectInfoWrapper.querySelector('.project-info') as HTMLDivElement;
		const projectInfoTaken = projectInfoWrapper.querySelector('.project-taken-info') as HTMLDivElement;

		const projectImage = this.step2.querySelector('.project-image img.first') as HTMLImageElement;
		const projectImage2 = this.step2.querySelector('.project-image img.second') as HTMLImageElement;
		const projectName = this.step2.querySelector('.project-excerpt h4') as HTMLHeadingElement;
		const projectExcerpt = this.step2.querySelector('.project-excerpt p') as HTMLParagraphElement;
		const plotTables = this.step2.querySelector('.tables') as HTMLDivElement;
		const plotRooms = this.step2.querySelector('.room-table') as HTMLTableElement;
		const projectMinPrice = this.step2.querySelector('.project-excerpt .min-price') as HTMLElement;
		const projectMinPlotPrice = this.step2.querySelector('.project-excerpt .min-plot-price') as HTMLElement;
		const projectMinPriceLabel = this.step2.querySelector('.project-excerpt .min-price-label') as HTMLSpanElement;

		const selectActions = this.step2.querySelector('#select-actions') as HTMLDivElement;
		const contactBtn = this.step2.querySelector('#contact-realter') as HTMLButtonElement;
		const selectBtn = this.step2.querySelector('#select-plot') as HTMLButtonElement;
		const buyPlotBtn = this.step2.querySelector('#buy-plot') as HTMLAnchorElement;
		const prebuiltBtn = this.step2.querySelector('#prebuilt-house') as HTMLButtonElement;

		const catalogBtn = this.step2.querySelector('#katalog-pdf') as HTMLAnchorElement;

		const tooltips = this.step2.querySelector('.tooltips') as HTMLDivElement;

		let selectedId: number = 0;

		let selectedPlotName = '';
		let selectedPlotImage = '';
		let selectedPlotImage2 = '';

		const moveImages = () => {
			const firstFloor = plotRooms.querySelector('[data-floor="1.np"]') as HTMLTableSectionElement;
			const secondFloor = plotRooms.querySelector('[data-floor="2.np"]') as HTMLTableSectionElement;
			if (secondFloor) {
				const firstFloorTop = firstFloor.getBoundingClientRect().y;
				const secondFloorTop = secondFloor.getBoundingClientRect().y;
				projectImage.style.height = `${secondFloorTop - firstFloorTop}px`;
			}
			else {
				projectImage.style.height = null;
			}
		};

		plots.forEach(plot => {
			const plotPrice = parseFloat(plot.dataset.plotPrice ?? '0.0');
			const basePrice = parseFloat(plot.dataset.basePrice ?? '0.0');
			const minPrice = parseFloat(plot.dataset.minPrice ?? '0.0');
			const rooms = plot.dataset.rooms;
			console.log(rooms);

			const plotObj = new Plot(
				plot,
				svg,
				projectInfoWrapper,
				projectImage,
				projectImage2,
				projectName,
				projectExcerpt,
				plotTables,
				plotRooms,
				tooltips,
			);
			plotObj.init();

			if (plot.classList.contains('selected')) {
				selectedId = plotObj.select();
				this.summary.basePrice = basePrice;
				this.summary.plotPrice = plotPrice;
				projectMinPrice.innerText = minPrice.toLocaleString(undefined, {
					style: 'currency', currency: 'CZK', maximumFractionDigits: 0,
				});
				projectMinPlotPrice.innerText = plotPrice.toLocaleString(undefined, {
					style: 'currency', currency: 'CZK', maximumFractionDigits: 0,
				});
				projectMinPlotPrice.parentElement.style.display = plotPrice > 0 ? null : 'none';
				if (plotObj.taken) {
					selectActions.style.display = 'none';
					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = 'none';
					selectBtn.style.display = 'none';
				} else if (plotObj.prebuilt) {
					selectActions.style.display = 'none';
					prebuiltBtn.style.display = 'inline-block';
					buyPlotBtn.style.display = 'none';
					selectBtn.style.display = 'none';
				} else if (plotObj.onlyplot) {
					selectActions.style.display = 'none';
					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = plotPrice > 0 ? 'inline-block' : 'none';
					selectBtn.style.display = 'none';
				} else {
					selectActions.style.display = 'grid';
					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = plotPrice > 0 ? 'inline-block' : 'none';
					selectBtn.style.display = 'inline-block';
				}
				catalogBtn.style.display = plot.dataset.catalog === '1' ? 'block' : 'none';
				this.selectedPlot = selectedId;
				catalogBtn.href = this.getUrl() + '&konfigurator-pdf=1&katalog=1';

				if (plot.dataset.href) {
					buyPlotBtn.href = plot.dataset.href;
				}

				selectedPlotName = plotObj.name;
				selectedPlotImage = plotObj.imageUrlFull;
				selectedPlotImage2 = plotObj.image2UrlFull;
				moveImages();
			}
			const selectPlot = () => {
				if (plotObj.taken) {
					plotObj.select();
					projectInfo.style.display = 'none';
					projectInfoTaken.style.display = 'block';

					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = 'none';
					selectBtn.style.display = 'none';
					return;
				}

				projectInfo.style.display = 'grid';
				projectInfoTaken.style.display = 'none';
				catalogBtn.style.display = plot.dataset.catalog === '1' ? 'block' : 'none';
				selectedId = plotObj.select();
				this.selectedPlot = selectedId;
				catalogBtn.href = this.getUrl() + '&konfigurator-pdf=1&katalog=1';
				this.summary.basePrice = basePrice;
				this.summary.plotPrice = plotPrice;
				this.summary.clearPrices().update();

				if (plotObj.prebuilt) {
					projectMinPriceLabel.innerText = 'Cena domu:';
				} else if (plotObj.onlyplot) {
					projectMinPriceLabel.innerText = 'Cena pozemku:';
				} else {
					projectMinPriceLabel.innerText = projectMinPriceLabel.dataset.basic ?? 'Cena parcely s domem BASIC:';
				}
				projectMinPrice.innerText = minPrice.toLocaleString(undefined, {
					style: 'currency', currency: 'CZK', maximumFractionDigits: 0,
				});
				projectMinPlotPrice.innerText = plotPrice.toLocaleString(undefined, {
					style: 'currency', currency: 'CZK', maximumFractionDigits: 0,
				});
				projectMinPlotPrice.parentElement.style.display = !plotObj.prebuilt && !plotObj.onlyplot && plotPrice > 0 ? null : 'none';


				if (plotObj.prebuilt) {
					prebuiltBtn.style.display = 'inline-block';
					buyPlotBtn.style.display = 'none';
					selectBtn.style.display = 'none';
				} else if (plotObj.onlyplot) {
					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = plotPrice > 0 ? 'inline-block' : 'none';
					selectBtn.style.display = 'none';
				} else {
					prebuiltBtn.style.display = 'none';
					buyPlotBtn.style.display = plotPrice > 0 ? 'inline-block' : 'none';
					selectBtn.style.display = 'inline-block';
				}

				if (plot.dataset.href) {
					buyPlotBtn.href = plot.dataset.href;
				}

				selectedPlotName = plotObj.name;
				selectedPlotImage = plotObj.imageUrlFull;
				selectedPlotImage2 = plotObj.image2UrlFull;

				moveImages();
			};

			plot.addEventListener('click', selectPlot);
			plot.addEventListener('touchstart', selectPlot);
		});

		selectBtn.addEventListener('click', () => {
			if (selectedId < 1 || selectBtn.style.display === 'none') {
				return;
			}

			this.selectPlot(selectedId);
			this.loadStep3().then(() => {
				this.initStep3();
			});
			this.stepIndicator.setStep(this.activeStep);
			this.updateHistory();
		});

		projectImage.addEventListener('click', () => {
			projectInfo.classList.toggle('expand-image');
			if (projectInfo.classList.contains('expand-image')) {
				projectImage.classList.add('active');
				projectImage2.classList.remove('active');
			}
			else {
				projectImage.classList.remove('active');
				projectImage2.classList.remove('active');
			}
		});

		projectImage2.addEventListener('click', () => {
			projectInfo.classList.toggle('expand-image');
			if (projectInfo.classList.contains('expand-image')) {
				projectImage.classList.remove('active');
				projectImage2.classList.add('active');
			}
			else {
				projectImage.classList.remove('active');
				projectImage2.classList.remove('active');
			}
		});

		window.addEventListener('resize', moveImages);
		this.resizeListeners.push(moveImages);

		contactBtn.addEventListener('click', () => {
			this.wrapper.classList.add('show-contact');
			window.scrollTo({
				top: this.wrapper.querySelector('.contact-wrapper').getBoundingClientRect().top + window.scrollY - 200,
				behavior: 'smooth',
			});
		});

		this.step2.querySelectorAll('.discount-banner').forEach(banner => {
			banner.addEventListener('click', () => {
				this.showDiscountPopup();
			});
		});

		this.stepIndicator.initStepClick(this.step2);
	}

	initStep3() {
		this.initCarousel();
		this.initBackBtn();
		this.clearResizeListeners();

		const seenHelp = window.localStorage.getItem('seenHelp');
		console.log(seenHelp, Date.now());
		if (!seenHelp || (seenHelp.length > 0 && parseInt(seenHelp) < Date.now())) {
			this.showHelpPopup();
			window.localStorage.setItem('seenHelp', (Date.now() + (3_600_000 * 24)).toString());
		}

		const equipmentWrapper = this.step3.querySelector('.equipment') as HTMLDivElement;

		const params = new URLSearchParams(equipmentWrapper.dataset.equipment ?? window.location.search);
		const errorMessage = this.step3.querySelector('.equipment-mandatory .error-message');

		const submitBtn = this.step3.querySelector<HTMLButtonElement>('#submit');
		console.log(submitBtn);
		submitBtn.addEventListener('click', () => {
			console.log('Submit!');
			if (this.validateStep3()) {
				this.activeStep = 4;
				this.loadStep4()
					.then(() => {
						this.initStep4();
					});
				this.stepIndicator.setStep(this.activeStep);
				this.updateHistory();
			} else {
				console.log('Validation failed');
			}
		});

		const stepContent = this.step3.querySelector('.step-content') as HTMLDivElement;
		let currMaxHeight = parseInt(window.getComputedStyle(this.step3).maxHeight.replace('px', ''));
		const maxHeightPadding = 1000;
		const updateBoxMaxHeight = () => {
			const height = stepContent.getBoundingClientRect().height;
			if ((currMaxHeight - maxHeightPadding) < height) {
				currMaxHeight = height + 1000;
				this.step3.style.maxHeight = `${currMaxHeight}px`;
			}
		};
		const resizeObserver = new ResizeObserver(updateBoxMaxHeight);
		resizeObserver.observe(stepContent);

		const categories = this.step3.querySelectorAll<HTMLTableRowElement>('.equipment-category');
		let previousCategory: EquipmentCategory | null = null;
		let rekuperace: EquipmentCategory | null = null;
		let termostat: EquipmentCategory | null = null;
		categories.forEach((category, key) => {
			const categoryObj = new EquipmentCategory(this.step3, category, this.summary);
			if (previousCategory) {
				previousCategory.nextCategory = categoryObj;
			}
			previousCategory = categoryObj;
			categoryObj.addChangeListener(() => window.history.replaceState({}, '', new URL(this.getUrl())));

			if (categoryObj.slug === 'rekuperace') {
				rekuperace = categoryObj;
			} else if (categoryObj.slug === 'termostaty-k-vytapeni') {
				termostat = categoryObj;
			}

			if (key === 0) {
				categoryObj.toggleShow();
				const closeDelay = () => {
					categoryObj.hide();
				};
				let timeout = setTimeout(closeDelay, 2000);
				const timeout2 = setTimeout(() => {
					document.addEventListener('scroll', stopTimeout);
				});
				document.addEventListener('mousemove', stopTimeout);

				function stopTimeout(e: Event) {
					console.log('stopTimeout', e);
					clearTimeout(timeout);
					clearTimeout(timeout2);
					document.removeEventListener('mousemove', stopTimeout);
					document.removeEventListener('scroll', stopTimeout);
				}
			}
		});

		if (rekuperace && termostat) {
			rekuperace.addChangeListener((price) => {
				if (price > 0) {
					termostat.checkPrice(0);
					termostat.disable();
				} else {
					termostat.enable();
				}
			});
			termostat.addChangeListener((price) => {
				if (price > 0) {
					rekuperace.checkPrice(0);
					rekuperace.disable();
				} else {
					rekuperace.enable();
				}
			});
		}

		this.summary.refresh();
		this.stepIndicator.initStepClick(this.step3);
	}

	initStep4(): void {
		this.initCarousel();
		this.initBackBtn();
		this.clearResizeListeners();

		const shareBtns = this.step4.querySelectorAll('.share-pdf') as NodeListOf<HTMLButtonElement>;
		shareBtns.forEach(btn => {
			if (!navigator.canShare && !navigator.clipboard) {
				btn.style.display = 'none';
			}
			const shareData = {
				title: 'OneTwoTree', text: btn.dataset.description, url: btn.dataset.href,
			};
			btn.addEventListener('click', () => {
				if (navigator.canShare) {
					navigator.share(shareData);
				} else if (navigator.clipboard) {
					navigator.clipboard.writeText(shareData.url)
						.then(r => {
							alert('Zkopírováno do schránky');
						});
				}
			});
		});

		(document.querySelectorAll('.popup-image') as NodeListOf<HTMLImageElement>).forEach(image => {
			const url = image.dataset.src;
			image.addEventListener('click', () => {
				this.showImagePopup(url, image.alt);
			});
		});

		const dialog = document.getElementById('configurator-send-pdf-dialog') as HTMLDialogElement;
		if (!dialog) {
			return;
		}

		dialog.addEventListener('click', e => {
			const rect = dialog.getBoundingClientRect();
			const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height && rect.left <= e.clientX && e.clientX <= rect.left + rect.width);
			if (!isInDialog) {
				dialog.close();
			}
		});

		const closeBtn = document.querySelector('.close-btn');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => {
				dialog.close();
			});
		}
		const form = dialog.querySelector('#send-pdf') as HTMLFormElement;
		console.log(form);
		if (!form) {
			return;
		}
		const messages = dialog.querySelector('.messages') as HTMLDivElement;
		const email = form.querySelector('#pdf-email') as HTMLInputElement;
		const submitBtn = form.querySelector('#submit-pdf-button') as HTMLButtonElement;
		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			const text = submitBtn.innerText;
			submitBtn.innerText = submitBtn.dataset.sending;
			submitBtn.disabled = true;
			const searchParams = new URLSearchParams;
			searchParams.set('mail', email.value);
			searchParams.set('action', 'konfigurator_send_pdf');
			const response = await fetch(
				esoul_konfigurator_params.ajaxUrl + `?mail=${email.value}` + this.getGetParams(),
				{
					method: 'post',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: searchParams,
				},
			);
			const data: { status: 'error' | 'ok', error?: string, [index: string]: any } = await response.json();
			submitBtn.disabled = false;
			submitBtn.innerText = text;
			if (response.ok && data.status === 'ok') {
				const message = document.createElement('div');
				message.classList.add('notice', 'notice-success');
				message.innerText = form.dataset.success;
				messages.innerHTML = '';
				messages.appendChild(message);
			} else {
				const message = document.createElement('div');
				message.classList.add('notice', 'notice-error');
				message.innerText = form.dataset.error;
				messages.innerHTML = '';
				messages.appendChild(message);
			}
		});
		const openBtn = document.getElementById('send-pdf-btn') as HTMLButtonElement;
		if (openBtn) {
			openBtn.addEventListener('click', () => {
				dialog.showModal();
			})
		}
	}

	private clearResizeListeners() {
		for (const listener of this.resizeListeners) {
			window.removeEventListener('resize', listener);
		}
		this.resizeListeners = [];
	}
	initContactForm() {
		const form = document.getElementById('konfigurator-contact-form') as HTMLFormElement;

		const messages = form.querySelector('.messages') as HTMLDivElement;
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const body = new URLSearchParams();
			body.append('nonce', esoul_konfigurator_params.nonce);
			(new FormData(form)).forEach((value, name) => {
				body.append(name, value.toString());
			});

			this.summary.prices.forEach((value, key) => {
				body.append(`equipment[${key}]`, value.toString());
			});

			const response = await fetch(esoul_konfigurator_params.ajaxUrl, {
				method: 'post', headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				}, body,
			});
			const data: { status: 'error' | 'ok', error?: string, [index: string]: any } = await response.json();

			if (response.ok && data.status === 'ok') {
				const message = document.createElement('div');
				message.classList.add('notice', 'notice-success');
				message.innerText = form.dataset.success;
				messages.innerHTML = '';
				messages.appendChild(message);
			} else {
				const message = document.createElement('div');
				message.classList.add('notice', 'notice-error');
				message.innerText = form.dataset.error;
				messages.innerHTML = '';
				messages.appendChild(message);
			}
		});
	}

	initBackBtn() {
		for (const btn of document.querySelectorAll<HTMLButtonElement>('.back-btn:not(.initialized)')) {
			btn.addEventListener('click', () => {
				switch (this.activeStep) {
					case 2:
						this.activeStep = 1;
						this.loadStep1().then(() => {
							this.initStep1();
							this.summary.clearPrices();
							this.stepIndicator.setStep(this.activeStep);
							this.updateHistory();
						});
						break;
					case 3:
						this.activeStep = 2;
						this.loadStep2().then(() => {
							this.initStep2();
							this.summary.clearPrices();
							this.stepIndicator.setStep(this.activeStep);
							this.updateHistory();
						});
						break;
					case 4:
						this.activeStep = 3;
						this.loadStep3().then(() => {
							this.initStep3();
						});
						this.stepIndicator.setStep(this.activeStep);
						this.updateHistory();
						break;
				}
			});
			btn.classList.add('initialized');
		}
	}

	private showImagePopup(url: string, alt: string = '') {
		this.popupImage.src = url;
		this.popupImage.alt = alt;

		this.imagePopup.showModal();
	}

	private showDiscountPopup() {
		// @ts-ignore
		window.elementorProFrontend.modules.popup.showPopup({id: 1297});
	}

	private showHelpPopup() {
		if (('elementorProFrontend' in window)
			// @ts-ignore
			&& ('modules' in window.elementorProFrontend)
			// @ts-ignore
			&& ('popup' in window.elementorProFrontend.modules)
			// @ts-ignore
			&& ('showPopup' in window.elementorProFrontend.modules.popup)) {
			// @ts-ignore
			window.elementorProFrontend.modules.popup.showPopup({id: 982});
		}
	}

	private validateStep3(): boolean {
		let valid = true;
		const categories = this.step3.querySelectorAll('.equipment-mandatory .equipment-category') as NodeListOf<HTMLDivElement>;
		for (const category of categories) {
			if (!category.classList.contains('selected')) {
				valid = false;
				category.classList.add('error');
			}
		}
		return valid;
	}

	private createLoader(): HTMLElement {
		const loader = document.createElement('div');
		loader.classList.add('loader-wrapper');
		loader.innerHTML = '<svg viewBox="0 0 100 100"><defs><circle id="loader" r="6" cx="50" cy="50" transform="translate(0 -30)"/></defs><g class="loader" transform="rotate(51 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(103 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(154 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(206 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(257 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(309 50 50)"><use xlink:href="#loader" /></g><g class="loader" transform="rotate(360 50 50)"><use xlink:href="#loader" /></g></svg>';
		return loader;
	}

	private async loadStep(step: number, params: { [index: string]: any } = {}) {
		params.action = 'konfigurator_step';
		params.step = step;
		params.nonce = esoul_konfigurator_params.nonce;

		const searchParams = new URLSearchParams;
		this.jsonToSearchParams(searchParams, params);
		return fetch(esoul_konfigurator_params.ajaxUrl, {
			method: 'POST', headers: {'Content-Type': 'application/x-www-form-urlencoded'}, body: searchParams,
		});
	}

	private jsonToSearchParams(params: URLSearchParams, data: { [index: string]: any }, currKey: string = ''): void {
		for (const key in data) {
			const combinedKey = currKey === '' ? key : currKey + `[${key}]`;

			if (typeof data[key] === 'object') {
				this.jsonToSearchParams(params, data[key], combinedKey);
				continue;
			}

			params.append(combinedKey, data[key]);
		}
	}

	private getUrl(): string {
		let url = window.location.origin + window.location.pathname + `?step=${this.activeStep}`;
		url += this.getGetParams();
		return url;
	}

	private getGetParams(): string {
		let url = '';
		if (this.selectedProject > 0) {
			url += `&project=${this.selectedProject}`;
		}
		if (this.selectedPlot > 0) {
			url += `&plot=${this.selectedPlot}`;
		}
		url += this.summary.getHistoryParams();
		return url;
	}

	private updateHistory(): void {
		const url = this.getUrl();
		window.history.pushState({}, '', new URL(url));
	}

	private initCarousel(): void {
		const carousels = this.wrapper.querySelectorAll('.carousel-wrapper:not(.initialized)') as NodeListOf<HTMLDivElement>;

		carousels.forEach(wrapper => {
			wrapper.classList.add('initialized');

			const next = wrapper.querySelector('.next') as HTMLButtonElement;
			const prev = wrapper.querySelector('.prev') as HTMLButtonElement;
			const carousel = wrapper.querySelector('.carousel') as HTMLDivElement;

			console.log(carousel, carousel.scrollWidth, wrapper.clientWidth, carousel.getBoundingClientRect());

			next.addEventListener('click', () => {
				if ((carousel.scrollLeft + wrapper.clientWidth) >= carousel.scrollWidth) {
					carousel.scrollLeft = 0;
				} else {
					carousel.scrollBy({left: wrapper.clientWidth});
				}
			});
			prev.addEventListener('click', () => {
				if ((carousel.scrollLeft - (wrapper.clientWidth / 2)) <= 0) {
					carousel.scrollLeft = carousel.scrollWidth;
				} else {
					carousel.scrollBy({left: -wrapper.clientWidth});
				}
			});
		});
	}
}
